import React, { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../hooks/storeHooks';
import { InstallInstallation } from '../../../../models/InstallModels';
import GenericFormTab from '../../../../components/common/GenericFormTab';
import { InstallInstallationFieldConfigs } from './InstallTabFieldConfig';
import { fetchWellIdentifier } from '../../../../services/installationHistory/InstallationHistoryService';

interface InstallInstallationTabProps {
  value: InstallInstallation;
  onChange: (value: InstallInstallation) => void;
  runlifeData?: {
    FailDateString?: Date | null;
    PullDateString?: Date | null;
    StartDateString?: Date | null;
  };
}

// Helper to determine status string
function getStatusString(
  failDate: Date | null,
  pullDate: Date | null,
  startDate: Date | null,
): InstallInstallation['Status'] {
  if (failDate) return 'Failed';
  if (pullDate && !failDate) return 'Pulled NF';
  if (startDate) {
    const now = new Date();
    const diff = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 3600 * 24));
    if (diff < 90) return 'Failed < 90 Days';
  }
  return 'Failed';
}

const InstallInstallationTab: React.FC<InstallInstallationTabProps> = ({ value, onChange, runlifeData }) => {
  const dispatch = useAppDispatch();

  const wellData = useAppSelector((state: any) => state.createInstalls.wellData.data) || [];
  const liftTypes = useAppSelector((state: any) => state.CreateCompletions.liftTypes.data) || [];
  const manufacturers = useAppSelector((state: any) => state.CreateCompletions.manufacturers.data) || [];
  const manufacturerControllers =
    useAppSelector((state: any) => state.CreateCompletions.manufactureControllers.data) || [];
  const assets = useAppSelector((state: any) => state.assetGroups?.assets) || [];

  const statusString = getStatusString(
    runlifeData?.FailDateString ?? null,
    runlifeData?.PullDateString ?? null,
    runlifeData?.StartDateString ?? null,
  );

  const selectedManufacturer = Array.isArray(manufacturers)
    ? manufacturers.find((m: any) => m.Id == value.Manufacturer)
    : undefined;

  const filteredManufacturerControllers =
    Array.isArray(manufacturerControllers) && selectedManufacturer
      ? manufacturerControllers.filter((mc: any) => mc.ManufacturerName === selectedManufacturer.Name)
      : [];

  const fieldConfigs = useMemo(() => {
    return InstallInstallationFieldConfigs.map((field) => {
      if (field.name === 'Well') {
        return {
          ...field,
          options: wellData.map((x: any) => ({ key: x.Value, value: x.Value })),
        };
      }
      if (field.name === 'LiftType') {
        return {
          ...field,
          options: liftTypes.map((x: any) => ({ key: x.Value, value: x.Value })),
        };
      }
      if (field.name === 'Manufacturer') {
        return {
          ...field,
          options: manufacturers.map((x: any) => ({ key: x.Id, value: x.Name })),
        };
      }
      if (field.name === 'ManufacturerController') {
        return {
          ...field,
          options: filteredManufacturerControllers.map((x: any) => ({
            key: x.ManufacturerControllerId,
            value: x.Name,
          })),
        };
      }
      if (field.name === 'Status') {
        return { ...field, type: 'text' as const, disabled: true };
      }
      return field;
    });
  }, [
    liftTypes,
    manufacturers,
    manufacturerControllers,
    filteredManufacturerControllers,
    value.Manufacturer,
    wellData,
  ]);

  const handleChange = (updated: InstallInstallation) => {
    let wellName = updated.Well;
    let wellId = 0;
    if (wellData && updated.Well) {
      const found = wellData.find((w: any) => w.Value === updated.Well);
      if (found) {
        wellName = found.Value;
        wellId = typeof found.Key === 'string' ? parseInt(found.Key, 10) : found.Key;
      }
    }
    if (wellName && assets.length > 0 && wellId !== 0) {
      const asset = assets.find((a: any) => a.assetName === wellName);
      if (asset) {
        dispatch(fetchWellIdentifier(asset.assetId)).then((result: any) => {
          const identifier = result?.payload?.Identifier || '';
          onChange({ ...updated, Well: wellName, WellId: wellId, WellIdentifier: identifier, Status: statusString });
        });
        return;
      }
    }
    onChange({ ...updated, Well: wellName, WellId: wellId, Status: statusString });
  };

  const patchedValue = { ...value, Well: value.Well, Status: statusString };

  return <GenericFormTab formData={patchedValue} setFormData={handleChange} fieldConfigs={fieldConfigs} />;
};

export default InstallInstallationTab;
